/**
 * Settings Pinia store — manages HoaSettings state for /settings/:section?.
 *
 * All mutations go through actions; never mutate state outside this store.
 * API calls use typed wrappers from @/api/settings.
 *
 * The `rosterLocked` computed is consumed by boardMembers.ts store to drive
 * the "Invite member" button disabled state (spec §5.3 / D-S4).
 *
 * The billing sub-section (`requestBillingPortal`) is owned by the settings-billing
 * slice (Agent C — Task 16) and creates a Stripe Customer Portal session.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { fetchSettings, patchProfile, patchPortal, patchBoardPolicy, patchAiPolicy, getLogoUploadUrl, confirmLogoUpload, requestExport, deleteWorkspace, cancelDelete, } from '@/api/settings';
export const useSettingsStore = defineStore('settings', () => {
    // ── State ─────────────────────────────────────────────────────────────────
    const profile = ref(null);
    const portal = ref(null);
    const boardPolicy = ref(null);
    const aiPolicy = ref(null);
    const billing = ref(null);
    const updatedAt = ref(null);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    // Soft-delete state (D-S9)
    const deletedAt = ref(null);
    const scheduledForHardDeleteAt = ref(null);
    // ── Computed ──────────────────────────────────────────────────────────────
    /** Read by boardMembers.ts to disable the Invite button when roster is locked (D-S4). */
    const rosterLocked = computed(() => boardPolicy.value?.rosterLocked ?? false);
    const isDeleted = computed(() => deletedAt.value !== null);
    // ── Actions ───────────────────────────────────────────────────────────────
    /** Load the full settings snapshot from GET /v1/settings. */
    async function fetch() {
        isLoading.value = true;
        error.value = null;
        try {
            const dto = await fetchSettings();
            profile.value = dto.profile;
            portal.value = dto.portal;
            boardPolicy.value = dto.boardPolicy;
            aiPolicy.value = dto.aiPolicy;
            billing.value = dto.billing;
            updatedAt.value = dto.updatedAt;
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load settings.';
        }
        finally {
            isLoading.value = false;
        }
    }
    /** PATCH /v1/settings/profile — optimistic update + rollback on error. */
    async function saveProfile(req) {
        if (!profile.value)
            return;
        const prev = { ...profile.value };
        // Optimistic update
        profile.value = {
            ...profile.value,
            name: req.name,
            shortCode: req.publicPortalSlug,
            portalUrl: `https://${req.publicPortalSlug}.hoawatch.us`,
            yearFounded: req.yearFounded ?? null,
            homeCount: req.residentCount,
            address: req.address ?? null,
        };
        isSaving.value = true;
        try {
            await patchProfile(req);
        }
        catch (e) {
            profile.value = prev; // rollback
            throw e;
        }
        finally {
            isSaving.value = false;
        }
    }
    /** PATCH /v1/settings/portal — optimistic toggle update + rollback. */
    async function savePortalToggle(field, value) {
        if (!portal.value)
            return;
        const prev = { ...portal.value };
        portal.value = { ...portal.value, [field]: value };
        isSaving.value = true;
        try {
            await patchPortal(portal.value);
        }
        catch (e) {
            portal.value = prev; // rollback
            throw e;
        }
        finally {
            isSaving.value = false;
        }
    }
    /** PATCH /v1/settings/board-policy — President only. */
    async function saveBoardPolicy(req) {
        const prev = boardPolicy.value ? { ...boardPolicy.value } : null;
        // Optimistic
        boardPolicy.value = {
            rosterLocked: req.rosterLocked,
            requireTwoFactor: req.requireTwoFactor,
            sessionTimeoutDays: req.sessionTimeoutDays,
            ipAllowlistEnabled: req.ipAllowlistEnabled,
            ipAllowlistJson: req.ipAllowlistJson ?? null,
            dualApprovalRequired: req.dualApprovalRequired,
        };
        isSaving.value = true;
        try {
            await patchBoardPolicy(req);
        }
        catch (e) {
            if (prev)
                boardPolicy.value = prev; // rollback
            throw e;
        }
        finally {
            isSaving.value = false;
        }
    }
    /** PATCH /v1/settings/ai-policy */
    async function saveAiPolicy(req) {
        const prev = aiPolicy.value ? { ...aiPolicy.value } : null;
        // Optimistic
        aiPolicy.value = {
            aiEnabled: req.aiEnabled,
            classifierEnabled: req.aiClassifierEnabled,
            summaryEnabled: req.aiSummaryEnabled,
        };
        isSaving.value = true;
        try {
            await patchAiPolicy(req);
        }
        catch (e) {
            if (prev)
                aiPolicy.value = prev; // rollback
            throw e;
        }
        finally {
            isSaving.value = false;
        }
    }
    /**
     * Upload a logo via the 3-step SAS-free flow (D-S2):
     * 1. POST /v1/settings/logo/upload-url → { uploadUrl, blobName }
     * 2. PUT file directly to Blob Storage
     * 3. POST /v1/settings/logo/confirm → logoUrl
     */
    async function uploadLogo(file) {
        isSaving.value = true;
        try {
            // Step 1: Get upload URL
            const { uploadUrl, blobName } = await getLogoUploadUrl();
            // Step 2: PUT file directly to blob (max 2 MB enforced server-side; also checked here)
            if (file.size > 2 * 1024 * 1024) {
                throw new Error('Logo file must be under 2 MB.');
            }
            const putResp = await window.fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
                body: file,
            });
            if (!putResp.ok) {
                throw new Error(`Blob upload failed: ${putResp.statusText}`);
            }
            // Step 3: Confirm the upload
            const logoUrl = await confirmLogoUpload(blobName);
            if (profile.value) {
                profile.value = { ...profile.value, logoUrl };
            }
        }
        finally {
            isSaving.value = false;
        }
    }
    /**
     * POST /v1/settings/export-request — D-S7: deferred.
     * Returns 501; catch and surface "Coming soon" at the component level.
     */
    async function requestDataExport() {
        await requestExport();
    }
    /**
     * DELETE /v1/settings/workspace — soft-delete. Sets local deletedAt state (D-S9).
     */
    async function deleteWorkspaceAction(confirmCode) {
        isSaving.value = true;
        try {
            const result = await deleteWorkspace(confirmCode);
            deletedAt.value = result.deletedAt;
            scheduledForHardDeleteAt.value = result.scheduledForHardDeleteAt;
        }
        finally {
            isSaving.value = false;
        }
    }
    /**
     * POST /v1/settings/cancel-delete — undo a pending soft-delete (D-S9 "Undo" button).
     */
    async function cancelDeleteAction() {
        isSaving.value = true;
        try {
            await cancelDelete();
            deletedAt.value = null;
            scheduledForHardDeleteAt.value = null;
        }
        finally {
            isSaving.value = false;
        }
    }
    /**
     * POST /v1/billing/portal-link — creates a Stripe Customer Portal session.
     * On success, redirects the browser to the Stripe-hosted portal.
     * Owned by settings-billing slice (Agent C — D-S8).
     */
    async function requestBillingPortal() {
        isSaving.value = true;
        error.value = null;
        try {
            const returnUrl = `${window.location.origin}/settings#billing`;
            const response = await window.fetch('/v1/billing/portal-link', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ returnUrl }),
            });
            if (!response.ok) {
                const body = (await response.json().catch(() => ({})));
                throw new Error(body['error'] ?? 'Failed to create billing portal session.');
            }
            const body = (await response.json());
            window.location.href = body.url;
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to open billing portal.';
            throw e; // allow BillingSection to show toast
        }
        finally {
            isSaving.value = false;
        }
    }
    function $reset() {
        profile.value = null;
        portal.value = null;
        boardPolicy.value = null;
        aiPolicy.value = null;
        billing.value = null;
        updatedAt.value = null;
        isLoading.value = false;
        isSaving.value = false;
        error.value = null;
        deletedAt.value = null;
        scheduledForHardDeleteAt.value = null;
    }
    return {
        // State
        profile,
        portal,
        boardPolicy,
        aiPolicy,
        billing,
        updatedAt,
        isLoading,
        isSaving,
        error,
        deletedAt,
        scheduledForHardDeleteAt,
        // Computed
        rosterLocked,
        isDeleted,
        // Actions
        fetch,
        saveProfile,
        savePortalToggle,
        saveBoardPolicy,
        saveAiPolicy,
        uploadLogo,
        requestDataExport,
        deleteWorkspace: deleteWorkspaceAction,
        cancelDelete: cancelDeleteAction,
        requestBillingPortal,
        $reset,
    };
});
